import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApp } from '../../../../app/AppContext.jsx';
import { useCatalog } from '../../../catalog/index.js';

import { useI18n } from '../../../../shared/i18n/useI18n.js';
import { useProductStrings } from '../../../../shared/i18n/useProductStrings.js';

import { useMobilePreviewCarousel } from '../../../../shared/hooks/useMobilePreviewCarousel.js';
import { resolveBuildingPreviewMode } from '../../../../shared/hooks/useSvgBuildingInteraction.js';
import { resolveProductPreviewMode } from '../../../../shared/hooks/useSvgProductInteraction.js';
import { resolveTierPreviewMode } from '../../../../shared/hooks/useSvgTierInteraction.js'
import { isDisplayable} from '../../../../shared/utils/productUtils.js';
import { PREVIEW_MODE_STRATEGY } from '../../../../shared/registries/outlinePreviewRegistry.js';
import { AVAILABILITY_STATUS } from '../../../../shared/constants/availabilityStatus.js';
import { HERO_OUTLINE } from '../../../../shared/constants/heroOutline.js';
import { OUTLINE_PREVIEW_MODE } from '../../../../shared/constants/outlinePreviewMode.js';
import { SECTION_ID } from '../../../../shared/constants/sectionIds.js';
import { VISIBILITY_STATUS } from '../../../../shared/constants/visibilityStatus.js';

import {
	getOutlineId,
} from '../../utils/outlinePreviewAdapter.js';

import { MobileProductCarouselSheet } from '../../../../shared/ui/MobileProductCarouselSheet/index.js';
import { OfferBadge } from '../../../../shared/ui/OfferBadge/OfferBadge.jsx';
import { Popover } from '../../../../shared/ui/Popover/index.js';
import { StatusBadge } from '../../../../shared/ui/StatusBadge/StatusBadge.jsx';

import styles from './PreviewOverlay.module.scss';
import { getTierName } from '../../../../shared/utils/productFormatters.js';

export function PreviewOverlay({ //TODO: move to shared domain
	activeTier = null,
	getMobileOutlineCandidates,
	getMobileProductCandidates,
	goToBuildingModel,
	goToTierSection,
	onRegisterPreviewHandler,
	svgContainerRef,
}) {
	const {
		appCapabilities,
		activeSectionId,
		isMobile,
		selectProductHandler,
	} = useApp();

	const {
		buildings,
		catalogCapabilities,
		tiers,
		products,
		filteredProducts,
		activeFiltersCount,
	} = useCatalog();

	const { t } = useI18n();
	const getProductsCategoryString = useProductStrings();

	// ── Local state ───────────────────────────────────────────────────────────

	const [previewData, setPreviewData] = useState(null);
	const [isSheetMinimized, setIsSheetMinimized] = useState(false); 

	const closeTimeoutRef = useRef(null);

	const handleClosePreview = useCallback(() => {
		clearTimeout(closeTimeoutRef.current);
		setPreviewData(null);
	}, []);

	// ── Derived state ───────────────────────────────────────────────────────────
	
  const {
		reservedUnitsAccessible,
		soldUnitsAccessible,
	} = appCapabilities;
	const areSoldAccessible = soldUnitsAccessible; //TODO: should boolean constants start with has*, are*, is* ?
	const areReservedAccessible = reservedUnitsAccessible; //TODO: should boolean constants start with has*, are*, is* ?

	const { 
    areSoldOutBuildingsActive,
		areSoldOutTiersActive,
    defaultProductCategory, 
    hasMultipleBuildings 
  } = catalogCapabilities;


	const displayableProducts = useMemo(
		() =>
			(products ?? []).filter((product) => isDisplayable(product.visibility)),
		[products],
	);

	const previewProduct = previewData?.product ?? null; 

	// ── Strategy context ───────────────────────────────────────────────────────────

	const strategyContext = useMemo(() => {
		const allProducts = displayableProducts;

		return {
			actions: {
				goToBuildingModel,
				goToTierSection,
				selectProductHandler,
			},
			activeFiltersCount,
			activeTier,
			allProducts,
			defaultProductCategory,
			filteredProducts: filteredProducts ?? [],
			getProductsCategoryString,
		};
	}, [
		activeFiltersCount,
		defaultProductCategory,
		displayableProducts,
		filteredProducts,
		getProductsCategoryString,
		goToBuildingModel,
		goToTierSection,
		selectProductHandler,
	]);

	const currentPreviewedId = useMemo(() => {
		if (!previewData) return null;
		if (previewData.product) return previewData.product.uuid;
		return getOutlineId(previewData);
	}, [previewData]);

	// ── Event manager ───────────────────────────────────────────────────────────

	const handleExecuteAction = useCallback(
		(mode, data) => {
			const strategy = PREVIEW_MODE_STRATEGY[mode];

			if (strategy && strategy.executeAction) {
				strategy.executeAction(data, strategyContext);
			}
		},
		[strategyContext],
	);

	const handlePreviewChange = useCallback((data) => {
		clearTimeout(closeTimeoutRef.current);
		if (data) {
			setPreviewData(data);
		} else {
			closeTimeoutRef.current = setTimeout(() => setPreviewData(null), 120);
		}
	}, []);

	useEffect(() => {
		if (svgContainerRef?.current) {
			const event = new CustomEvent('bim:preview-change', {
				detail: currentPreviewedId,
			});
			svgContainerRef.current.dispatchEvent(event);
		}
	}, [currentPreviewedId, svgContainerRef]);

	// ── Stable foo passed to parent ──────────────────────────
	useEffect(() => {
		onRegisterPreviewHandler?.({
			handleExecuteAction,
			handlePreviewChange,
			handleClosePreview,
		});
	}, [
		onRegisterPreviewHandler,
		handleExecuteAction,
		handleClosePreview,
		handlePreviewChange,
	]);


  // ── Outline carousel (buildings / tiers / products mobile swipe) ──────────────────────────────────────────────────

	const mobileOutlineCandidates = useMemo(() => {
		return getMobileOutlineCandidates ? getMobileOutlineCandidates(previewData?.type) : [];
	}, [getMobileOutlineCandidates]);

	const mobileProductCandidates = useMemo(() => {
		return getMobileProductCandidates ? getMobileProductCandidates() : [];
	}, [getMobileProductCandidates]);

	const activeCandidates = previewProduct
		? mobileProductCandidates
		: mobileOutlineCandidates;

	const {
		visibleProductsByUuid,
		swipeOffset,
		swipeActionSide,
		onSwipeMove,
		markSwipePrev,
		markSwipeNext,
		resetSwipeUi,
	} = useMobilePreviewCarousel({
		products: products ?? [],
		filteredProducts: filteredProducts ?? [],
		effectiveBuilding: previewProduct?.building_id ?? null,
		activeTier: previewProduct?.ref_tier ?? null,
		previewProduct,
		isSheetMinimized,
		carouselCandidates: activeCandidates,
		hasActiveItem: !!previewData,
	});

  // ── Preview mode resolvers for carousel outline switch ────────────────────────────────────────────────────────────

	const resolvePreviewModeForProduct = useCallback((product) => {
    const building = buildings?.find((b) => b.id_cad === product.building_id);
		const isInOtherStage = building?.currently_on_sale === false;

		return resolveProductPreviewMode({
			isInOtherStage,
        building,
				isVisible: visibleProductsByUuid.has(product.uuid),
				isSold: product.status === AVAILABILITY_STATUS.SOLD,
				isReserved: product.status === AVAILABILITY_STATUS.RESERVED,
				visibility: product.visibility,
				areSoldAccessible,
				areReservedAccessible,
			})},
		[
      areReservedAccessible, 
      areSoldAccessible,
      buildings,
      visibleProductsByUuid],
	);

  const resolvePreviewModeForOutline = useCallback((candidate) => {
		const { type, building, tier } = candidate;

		if (type === HERO_OUTLINE.TIERS) {
			return resolveTierPreviewMode({
				tier,
				building,
				areSoldOutTiersActive,
				soldUnitsAccessible,
			});
		}

		if (type === HERO_OUTLINE.BUILDINGS) {
			return resolveBuildingPreviewMode({
				building,
				areSoldOutBuildingsActive,
				soldUnitsAccessible,
			});
		}

		return OUTLINE_PREVIEW_MODE.NORMAL_OUTLINE;
	}, [areSoldOutTiersActive, areSoldOutBuildingsActive, soldUnitsAccessible]);

  // ── Carrousel navigation ──────────────────────────────────────────────────────────────────────────────────────────

	const handleSwipeNext = useCallback(() => {
		setPreviewData((prev) => {
			if (!prev) return prev;
			const isProd = !!prev.product;

			// Wywołujemy odpowiedni getter w locie
			const candidates = isProd
				? getMobileProductCandidates?.()
				: getMobileOutlineCandidates?.(prev.type);
			if (!candidates?.length) return prev;

			const currentId = isProd ? prev.product.uuid : getOutlineId(prev);
			const currentIndex = candidates.findIndex((c) =>
				isProd ? c.uuid === currentId : getOutlineId(c) === currentId,
			);
			if (currentIndex < 0) return prev;

			const nextIndex = (currentIndex + 1) % candidates.length;
			const nextItem = candidates[nextIndex];
      const nextBuilding = isProd 
        ? buildings?.find(b => b.id_cad === nextItem.building_id) || prev.building 
        : null;
      
      const nextTier = isProd 
        ? tiers?.find(t => t.ref_tier === nextItem.ref_tier && t.building_id === nextItem.building_id) || prev.tier 
        : null;


			return isProd
				? {
						...prev,
						product: nextItem,
            building: nextBuilding,
            tier: nextTier,        
            mode: resolvePreviewModeForProduct(nextItem),
          }
				: {
            ...prev,
            ...nextItem,
            mode: resolvePreviewModeForOutline(nextItem)
          };
		});
		markSwipeNext();
	}, [
    buildings,
		getMobileProductCandidates,
		getMobileOutlineCandidates,
		markSwipeNext,
    tiers,
    resolvePreviewModeForOutline,
		resolvePreviewModeForProduct,
	]);

	const handleSwipePrev = useCallback(() => {
		setPreviewData((prev) => {
			if (!prev) return prev;
			const isProd = !!prev.product;

			const candidates = isProd
				? getMobileProductCandidates?.()
				: getMobileOutlineCandidates?.(prev.type);
			if (!candidates?.length) return prev;

			const currentId = isProd ? prev.product.uuid : getOutlineId(prev);
			const currentIndex = candidates.findIndex((c) =>
				isProd ? c.uuid === currentId : getOutlineId(c) === currentId,
			);
			if (currentIndex < 0) return prev;

			const nextItem =
				candidates[(currentIndex - 1 + candidates.length) % candidates.length];
      const nextBuilding = isProd 
        ? buildings?.find(b => b.id_cad === nextItem.building_id) || prev.building 
        : null;
      
      const nextTier = isProd 
        ? tiers?.find(t => t.ref_tier === nextItem.ref_tier && t.building_id === nextItem.building_id) || prev.tier 
        : null;


			return isProd
				? {
						...prev,
						product: nextItem,
            building: nextBuilding,
            tier: nextTier,        
            mode: resolvePreviewModeForProduct(nextItem),
          }
				: {
            ...prev,
            ...nextItem,
            mode: resolvePreviewModeForOutline(nextItem)
          };
		});
		markSwipePrev();
	}, [
    buildings,
		getMobileProductCandidates,
		getMobileOutlineCandidates,
		markSwipePrev,
    tiers,
    resolvePreviewModeForOutline,
		resolvePreviewModeForProduct,
	]);
  
  
  // ── Mobile cleanup on section leave ─────────────────────────────────────────────────────────────────────────────
	
  useEffect(() => {
		if (!isMobile) return;
		if (activeSectionId === SECTION_ID.HERO) return;
		setPreviewData(null);
	}, [activeSectionId, isMobile]);
  
	useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);
  
  // ── Reset UI state on item change ─────────────────────────
  useEffect(() => {
    setIsSheetMinimized(false);
    resetSwipeUi();
  }, [currentPreviewedId, resetSwipeUi]);


	// ── Strategy resolution ───────────────────────────────────────────────────────────────────────────────────────────

	const previewConfig = useMemo(() => {
		if (!previewData) return null;

    const mode = previewData.product
			? resolvePreviewModeForProduct(previewData.product)
			: resolvePreviewModeForOutline(previewData);

		const strategy =
			PREVIEW_MODE_STRATEGY[mode] ??
			PREVIEW_MODE_STRATEGY[OUTLINE_PREVIEW_MODE.NORMAL];

		if (!strategy || !strategy.component) return null;

		return {
			Component: strategy.component,
      MobileComponent: strategy.mobileComponent || strategy.component,
			props: strategy.getProps({...previewData, mode}, strategyContext),
		};
}, [
		previewData, 
		strategyContext, 
		resolvePreviewModeForProduct, 
		resolvePreviewModeForOutline
	]);

	// ── Render helpers and props ──────────────────────────────────────────────────────────────────────────────────────
  
  const getMobileSheetTitle = () => {
    if (!previewData || !previewConfig) return null;

    const building = previewData.building;
    if(!building.currently_on_sale) return null;

    const buildingTitle = `${t('common.building')} ${building.name_original ?? building.id_cad}`

		if (previewData.type === HERO_OUTLINE.BUILDINGS) {
			return buildingTitle
    }
		if (previewData.type === HERO_OUTLINE.TIERS) {
      const tier = previewData.tier;
      const tierTitle = `${t('common.floor')}: ${getTierName(tier, true)}`;
      const buildingAdage = hasMultipleBuildings ? `, ${buildingTitle}` : '';
      return `${tierTitle}${buildingAdage}`
    }

    if (previewProduct) return previewProduct.name_cms ?? previewProduct.id_original //TODO add this to some kind od shared hook to get resolved product name
  }

	const renderMobileHeaderBadges = () => {
		if (!previewProduct) return null;
		if (previewData?.building?.currently_on_sale === false) return null;

		return (
			<>
				{previewProduct?.visibility === VISIBILITY_STATUS.VISIBLE && (
					<StatusBadge status={previewProduct?.status} size="small" />
				)}
				{previewProduct?.price_offer?.on_offer && <OfferBadge size="tiny" />}
			</>
		);
	};

  // ── Render ────────────────────────────────────────────────────────────────────────────────────────────────────────

  if (!previewData || !previewConfig) return null;

	return (
		<>
			{/* ── Desktop Popover ── */}
			{!isMobile && (
				<Popover
					isOpen
					initialCursorPos={previewData?.cursorPos ?? null}
					onClose={handleClosePreview}
				>
					<previewConfig.Component {...previewConfig.props} />
				</Popover>
			)}

			{/* ── Mobile Carousel Sheet ── */}
			{isMobile && (
				<MobileProductCarouselSheet
          active={previewProduct ? previewProduct : previewData} //not needed?
					isOpen
					onClose={handleClosePreview}
					isMinimized={isSheetMinimized}
					onMinimize={() => setIsSheetMinimized(true)}
					onExpand={() => setIsSheetMinimized(false)}
					onNavigatePrev={handleSwipePrev}
					onNavigateNext={handleSwipeNext}
          headerTitle={getMobileSheetTitle()}
					headerBadges={renderMobileHeaderBadges()}
					bottomInset="var(--bim-mobile-nav-inset-bottom)"
					className={styles.mobilePreviewSheet}
					mode={previewData?.mode} //not needed?
					swipeOffset={swipeOffset}
					swipeActionSide={swipeActionSide}
					onSwipeMove={onSwipeMove}
					renderProductContent={() => (
						<previewConfig.MobileComponent
							{...previewConfig.props}
							className={styles.mobilePreviewBodyCard}
						/>
					)}
				/>
			)}
		</>
	);
}
